import Head from 'next/head'
import Script from 'next/script'
import styled from 'styled-components'
import GlobalStyles from './styles'
import Header from './Header'
import Footer from './Footer'
import getConfig from 'next/config'
import OneTrustButtons from '../components/OneTrustButtons'
import OneTrustScript from '../components/OneTrustScript'
import StackAdapt from '../components/StackAdapt'
import MetaPixelScript from '../components/MetaPixelScript'

// Per-locale layout cache — header, footer, and translations rarely change.
// 12h TTL matches ISR revalidate. Shared across all pages in the same process.
const layoutCache = new Map<string, { data: any; expiresAt: number }>()
const LAYOUT_CACHE_TTL_MS = 12 * 60 * 60 * 1000

/** @internal — exposed for test isolation only */
export const _clearLayoutCache = () => layoutCache.clear()


export const layoutData = async (Stack: any, locale: string) => {
  const cacheKey = locale.toLowerCase()
  const cached = layoutCache.get(cacheKey)
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data
  }

  const { publicRuntimeConfig } = getConfig()
  const [header, footer, translations]: any[] = await Promise.all([
    Stack.getEntryHeader('header', locale, [
      'navigation_menu.page_reference',
      ...(publicRuntimeConfig?.additionalHeaderReferences || [])
    ]),
    Stack.getEntry('footer', locale),
    Stack.getEntry('translations', locale),
  ])

  // withFallback returns null when CMS is unreachable and no stale cache exists.
  // Indexing null throws TypeError — guard each array before indexing.
  // Use null (not {}) as the fallback so consumers can check for null rather
  // than receiving a fake empty object that crashes on property access.
  // Only cache when all three entries were successfully fetched — caching a
  // null fallback for 12h would serve a broken layout to every user for the
  // entire TTL window after a CMS outage.
  const headerData = (header ?? [])[0] ?? null
  const footerData = (footer ?? [])[0] ?? null
  const translationsData = (translations ?? [])[0] ?? null

  const data = {
    header: headerData,
    footer: footerData,
    translations: translationsData
  }

  if (headerData && footerData && translationsData) {
    layoutCache.set(cacheKey, { data, expiresAt: Date.now() + LAYOUT_CACHE_TTL_MS })
  }

  return data
}

export const layoutProps = async (api: any, locale: string, props?: any, revalidate: number = 43200) => {
  return {
    props: {
      layout: true,
      ...(await layoutData(api, locale)),
      ...props
    },
    revalidate
  }
}

const metaData = (meta: any) => {
  const metaArr = []
  for (const key in meta) {
    if (key !== 'enable_search_indexing') {
      metaArr.push(<meta name={key.includes('meta_') ? key.split('meta_')[1] : key} content={meta[key]} key={key} />)
    }
  }
  return metaArr
}

const Layout = ({
  header,
  footer,
  seo: meta,
  translations,
  children,
  locale,
  Styles,
  gtm,
  gtm2,
  lcp_image
}: any) => {
  return (
    <>
      <Head>
        <link rel='icon' href='/favicon.ico' />
        <meta name='viewport' content='width=device-width,initial-scale=1,minimum-scale=1' />
        <title>{(meta?.title || meta?.meta_title) || header?.title}</title>
        <meta name='robots' content='index, follow' />
        {meta && meta.enable_search_indexing && metaData(meta)}
        {lcp_image && (
          <link
            rel='preload'
            as='image'
            href={`${lcp_image}?height=672&auto=webp&format=pjpg&quality=75`}
            media='(min-width: 768px)'
            {...{ fetchpriority: 'high' } as any}
          />
        )}


        {translations?.pixels?.ttd_pixels && translations?.pixels?.ttd_pixels_2 &&
          <script
            dangerouslySetInnerHTML={{
              __html: `
                (async () => {
                  try {                    
                    const loadScript = (src) => new Promise((resolve, reject) => {
                      const script = document.createElement('script');
                      script.type = 'text/javascript';
                      script.src = src;
                      script.onload = () => {
                        resolve();
                      };
                      script.onerror = (error) => {
                        console.error('Script failed to load:', src, error);
                        reject(error);
                      };
                      document.head.appendChild(script);
                    });

                    await loadScript('https://js.adsrvr.org/up_loader.1.1.0.js');
                    
                    if (typeof ttd_dom_ready === 'function') {
                      ttd_dom_ready(() => {
                        if (typeof TTDUniversalPixelApi === 'function') {
                          const universalPixelApi = new TTDUniversalPixelApi();
                          universalPixelApi.init(
                            "${translations?.pixels?.ttd_pixels}",
                            ["${translations?.pixels?.ttd_pixels_2}"],
                            "https://insight.adsrvr.org/track/up"
                          );
                          console.info('TTD pixel initialized successfully');
                        } else {
                          console.warn('TTDUniversalPixelApi not found');
                        }
                      });
                    } else {
                      console.warn('ttd_dom_ready function not found');
                    }
                  } catch (error) {
                    console.error('TTD pixel failed to load:', error);
                  }
                })();
              `
            }}
          />
        }
        {translations?.pixels?.bellxandr_pixels &&
          <>
            <script dangerouslySetInnerHTML={{
              __html: `
            !function(e,i){if(!e.pixie){var n=e.pixie=function(e,i,a){n.actionQueue.push({action:e,actionValue:i,params:a})};n.actionQueue=[];var a=i.createElement("script");a.async=!0,a.src="//acdn.adnxs.com/dmp/up/pixie.js";var t=i.getElementsByTagName("head")[0];t.insertBefore(a,t.firstChild)}}(window,document);
            pixie('init', '${translations?.pixels?.bellxandr_pixels}');
            pixie('event', 'PageView');
            `
            }}>
            </script>
            <noscript>
              <img alt={'Xandr Universal Pixel'}
                   src={`https://ib.adnxs.com/pixie?pi=${translations?.pixels?.bellxandr_pixels}&e=PageView&script=0`}
                   width='1' height='1' style={{ display: 'none' }} />
            </noscript>
          </>
        }
        <OneTrustScript />
        <StackAdapt />
        <MetaPixelScript />
      </Head>

      {/* GTM — afterInteractive defers until after hydration, off the critical path */}
      {gtm && (
        <>
          <Script
            id='gtm-init'
            strategy='afterInteractive'
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
              `
            }}
          />
          <Script
            id='gtm-script'
            strategy='afterInteractive'
            src={`https://www.googletagmanager.com/gtm.js?id=${gtm}`}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" title="gtm" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
            }}
          />
        </>
      )}
      {gtm2 && (
        <>
          <Script
            id='gtag-script'
            strategy='afterInteractive'
            src={`https://www.googletagmanager.com/gtag/js?id=${gtm2}`}
          />
          <Script
            id='gtag-init'
            strategy='afterInteractive'
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                window.gtag = gtag;
                gtag('js', new Date());
                gtag('config', '${gtm2}');
              `
            }}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm2}" title="gtm" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
            }}
          />
        </>
      )}
      {translations?.yotpo?.app_key && (
        <Script
          id='yotpo-script'
          strategy='afterInteractive'
          src={`https://staticw2.yotpo.com/${translations.yotpo.app_key}/widget.js`}
        />
      )}
      <GlobalStyles />
      <Styles locale={locale} />
      <Skip href='#main-content'>Skip Navigation</Skip>
      <Header {...{ header, translations }} />
      {translations?.pixels?.roku_pixels &&
        <img alt={'Roku Pixel'}
             src={`https://tags.w55c.net/rs?id=${translations?.pixels?.roku_pixels}&t=homepage`}
             style={{ width: 0, height: 0, display: 'none' }}
        />
      }
      <Main id='main-content' tabIndex={-1}>
        <h1 className='sr-only'>{header?.title}</h1>
        <div style={{ flex: 0 }}>{children}</div>
      </Main>
      {footer && <Footer {...{ footer }} />}
      <OneTrustButtons />
    </>
  )
}

export default Layout

const Main = styled.main`
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  outline: none;
`

const Skip = styled.a`
  left: -10000px;
  position: absolute;
  z-index: 1;

  &:focus {
    left: 0;
  }
`
